import React from 'react'
import clsx from 'clsx'
import { Lodash } from 'lang-utils'
import { Logger } from 'wdc-cube'
import { keyframes } from 'tss-react'
import { makeStyles } from 'tss-react/mui'

import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'

import DownloadIcon from '@mui/icons-material/Download'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

import { FCClassContext, chipSx, classToFComponent } from 'src/utils/views'

import {
    OverlayScope,
    FilterEntryScope,
    OverlayLivreScope,
    OverlayEmpresaScope,
    OverlayPessoaScope,
    OverlaySocioScope,
    OverlayCnesScope,
    OverlayPessoaAndSocioScope,
    OverlayOperadoraScope
} from '../ta_scopes'

const LOG = Logger.get('TA-OVERLAY-VIEW')

const lodash = Lodash.singleton

const filterChipSx = {
    ...chipSx('gray'),
    maxWidth: '100%',
    height: 'auto',
    minHeight: 25,
    '& .MuiChip-label': {
        display: 'block',
        padding: '4px 9px',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        lineHeight: 1.35
    },
    '& .MuiChip-deleteIcon': {
        fontSize: 16,
        color: '#8b948f',
        '&:hover': { color: 'var(--danger)' }
    }
}

const useStyles = makeStyles()({
    view: {
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'row'
    },
    buttonBar: {
        pointerEvents: 'auto',
        marginTop: 30,
        alignSelf: 'start',
        display: 'flex',
        flexDirection: 'column',
        gap: 10
    },
    controlButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 40,
        width: 40,
        cursor: 'pointer',
        color: '#2d332f',
        backgroundColor: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRight: 0,
        borderStartStartRadius: 'var(--radius)',
        borderEndStartRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-1)',
        outline: 'none',
        transition: 'background-color .15s ease, color .15s ease',
        ':focus, :hover': {
            color: 'var(--green-800)',
            backgroundColor: 'var(--green-050)'
        }
    },
    controlButtonActive: {
        color: 'var(--green-800)',
        backgroundColor: 'var(--green-050)'
    },
    body: {
        pointerEvents: 'auto',
        backgroundColor: 'var(--surface)',
        color: 'var(--ink)',
        border: '1px solid var(--border)',
        borderRight: 0,
        borderStartStartRadius: 'var(--radius)',
        borderEndStartRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-1)',
        overflowY: 'auto',
        padding: 12,

        display: 'flex',
        flexDirection: 'column',
        rowGap: 10
    },
    bodyOpen: {
        width: 250
    },
    bodyClose: {
        width: 0,
        display: 'none'
    },
    bodyOpenAnimation: {
        animation: `${keyframes`
        from  {
            width: 0;
        }
        to {
            width: 250px;
        }
        `} 0.1s forwards ease-in`
    },
    bodyCloseAnimation: {
        animation: `${keyframes`
        from  {
            width: 250px;
        }
        to {
            width: 0;
            display: none;
        }
        `} 0.1s forwards ease-in`
    },
    panelTitle: {
        margin: 0,
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.2,
        color: 'var(--ink)',
        flexShrink: 0
    },
    filterGroup: {
        padding: 12,
        border: '1px solid var(--border)',
        borderRadius: 11,
        backgroundColor: '#fbfcfb',
        display: 'flex',
        flexDirection: 'column',
        rowGap: 8,
        flexShrink: 0
    },
    filterGroupTitle: {
        margin: 0,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '.01em',
        color: '#303733'
    },
    chips: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 7,
        flexWrap: 'wrap'
    },
    chipTag: {
        color: 'var(--muted)',
        fontWeight: 700
    },
    downloadProgress: {
        color: 'var(--green-700)'
    }
})

type OverlayViewProps = {
    className?: string
    scope: OverlayScope
}

class OverlayViewClass implements FCClassContext<OverlayViewProps> {
    // :: Fields
    scope: OverlayScope
    prevExpanded = false

    constructor({ scope }: OverlayViewProps) {
        this.scope = scope
        this.prevExpanded = scope.expanded
    }

    // :: Emissors

    readonly emitFilterToggleClick = () => this.scope.onFilterToggleClick().catch(LOG.caught)
    readonly emitDownloadClick = () => this.scope.onDownloadClick().catch(LOG.caught)

    // :: Methods

    render({ className, scope }: OverlayViewProps) {
        this.scope = scope

        const classes = useStyles().classes
        let bodyExpanded = scope.expanded ? classes.bodyOpen : classes.bodyClose

        if (this.prevExpanded !== scope.expanded) {
            bodyExpanded = this.prevExpanded ? classes.bodyCloseAnimation : classes.bodyOpenAnimation
            this.prevExpanded = scope.expanded
        }

        // prettier-ignore
        return (
            <div className={clsx(classes.view, className)}>
                <div className={classes.buttonBar}>
                    <Tooltip title={scope.expanded ? 'Ocultar filtros aplicados' : 'Ver filtros aplicados'} placement="left">
                        <a
                            className={clsx(classes.controlButton, scope.expanded && classes.controlButtonActive)}
                            aria-label={scope.expanded ? 'Ocultar filtros aplicados' : 'Ver filtros aplicados'}
                            onClick={this.emitFilterToggleClick}>
                            <FilterAltIcon fontSize="small" />
                        </a>
                    </Tooltip>
                    {scope.downloadEnabled &&
                        <Tooltip title="Exportar o recorte atual" placement="left">
                            <a
                                className={classes.controlButton}
                                aria-label="Exportar o recorte atual"
                                onClick={this.emitDownloadClick}>
                                {!scope.downloading && <DownloadIcon fontSize="small" />}
                                {scope.downloading && <CircularProgress className={classes.downloadProgress} size={18} />}
                            </a>
                        </Tooltip>
                    }
                </div>
                <div className={clsx(classes.body, bodyExpanded)}>
                    <h3 className={classes.panelTitle}>Filtros aplicados</h3>
                    {scope.livre.hasContent()          && <FilterForLivreView          scope={scope.livre} />}
                    {scope.empresa.hasContent()        && <FilterForEmpresaView        scope={scope.empresa} />}
                    {scope.cnes.hasContent()           && <FilterForCnesView           scope={scope.cnes} />}
                    {scope.socios.hasContent()         && <FilterForSocioView          scope={scope.socios} />}
                    {scope.pessoa.hasContent()         && <FilterForPessoaView         scope={scope.pessoa} />}
                    {scope.pessoaAndSocio.hasContent() && <FilterForPessoaAndSocioView scope={scope.pessoaAndSocio} />}
                    {scope.operadoras.hasContent()     && <FilterForOperadoraView      scope={scope.operadoras} />}
                </div>
            </div>
        )
    }
}
export const OverlayView = classToFComponent(OverlayViewClass)
export default OverlayView

//////////////

type FilterGroupProps = {
    className?: string
    title: string
    children?: React.ReactNode
}

function FilterGroup({ className, title, children }: FilterGroupProps) {
    const classes = useStyles().classes
    return (
        <div className={clsx(classes.filterGroup, className)}>
            <h4 className={classes.filterGroupTitle}>{title}</h4>
            <div className={classes.chips}>{children}</div>
        </div>
    )
}

//////////////

type FilterEntryProps = {
    className?: string
    tag?: string
    scope: FilterEntryScope
}

class FilterEntryViewClass implements FCClassContext<FilterEntryProps> {
    scope!: FilterEntryScope

    // :: Methods

    emitRemoveClick = () => {
        this.scope.onRemoveClick().catch(LOG.caught)
    }

    render({ className, tag, scope }: FilterEntryProps) {
        this.scope = scope

        const classes = useStyles().classes

        const label = (
            <>
                {lodash().isString(tag) && <span className={classes.chipTag}>{tag}: </span>}
                {scope.label}
            </>
        )

        return (
            <Chip className={className} size="small" label={label} sx={filterChipSx} onDelete={this.emitRemoveClick} />
        )
    }
}
const FilterEntryView = classToFComponent(FilterEntryViewClass)

//////////////

type LivreViewProps = {
    className?: string
    scope: OverlayLivreScope
}

class FilterForLivreViewClass implements FCClassContext<LivreViewProps> {
    scope!: OverlayLivreScope

    // :: Methods

    render({ className, scope }: LivreViewProps) {
        this.scope = scope

        return (
            <FilterGroup className={className} title="Global">
                {scope.texts && scope.texts.map((s) => <FilterEntryView key={s.id} scope={s} />)}
            </FilterGroup>
        )
    }
}
const FilterForLivreView = classToFComponent(FilterForLivreViewClass)

//////////////

type FilterForEmpresaViewProps = {
    className?: string
    scope: OverlayEmpresaScope
}

class FilterForEmpresaViewClass implements FCClassContext<FilterForEmpresaViewProps> {
    scope!: OverlayEmpresaScope

    // :: Methods

    render({ className, scope }: FilterForEmpresaViewProps) {
        this.scope = scope

        // prettier-ignore
        return (
            <FilterGroup className={className} title="Empresa">
                {scope.cnpjs && scope.cnpjs.map((s) => <FilterEntryView key={s.id} tag="CNPJ" scope={s} />)}
                {scope.empresas && scope.empresas.map((s) => <FilterEntryView key={s.id} tag="Nome" scope={s} />)}
                {scope.cnaes && scope.cnaes.map((s) => <FilterEntryView key={s.id} tag="CNAE" scope={s} />)}
                {scope.empNatureza && scope.empNatureza.map((s) => <FilterEntryView key={s.id} tag="Nat.Jurídica" scope={s} />)}
                {scope.empEnderecos && scope.empEnderecos.map((s) => <FilterEntryView key={s.id} tag="Endereço" scope={s} />)}
                {scope.empLogradouros && scope.empLogradouros.map((s) => <FilterEntryView key={s.id} tag="Rua" scope={s} />)}
                {scope.empBairros && scope.empBairros.map((s) => <FilterEntryView key={s.id} tag="Bairro" scope={s} />)}
                {scope.empMunicipios && scope.empMunicipios.map((s) => <FilterEntryView key={s.id} tag="Cidade" scope={s} />)}
                {scope.empCeps && scope.empCeps.map((s) => <FilterEntryView key={s.id} tag="CEP" scope={s} />)}
                {scope.empUfs && scope.empUfs.map((s) => <FilterEntryView key={s.id} tag="UF" scope={s} />)}
            </FilterGroup>
        )
    }
}
const FilterForEmpresaView = classToFComponent(FilterForEmpresaViewClass)

//////////////

type FilterForPessoaViewProps = {
    className?: string
    scope: OverlayPessoaScope
}

class FilterForPessoaViewClass implements FCClassContext<FilterForPessoaViewProps> {
    scope!: OverlayPessoaScope

    // :: Methods

    render({ className, scope }: FilterForPessoaViewProps) {
        this.scope = scope

        // prettier-ignore
        return (
            <FilterGroup className={className} title="Pessoa">
                {scope.pessoas && scope.pessoas.map((s) => <FilterEntryView key={s.id} tag="Nome" scope={s} />)}
                {scope.pesEnderecos && scope.pesEnderecos.map((s) => <FilterEntryView key={s.id} tag="Endereço" scope={s} />)}
                {scope.pesLogradouros && scope.pesLogradouros.map((s) => <FilterEntryView key={s.id} tag="Rua" scope={s} />)}
                {scope.pesBairros && scope.pesBairros.map((s) => <FilterEntryView key={s.id} tag="Bairro" scope={s} />)}
                {scope.pesMunicipios && scope.pesMunicipios.map((s) => <FilterEntryView key={s.id} tag="Cidade" scope={s} />)}
                {scope.pesCeps && scope.pesCeps.map((s) => <FilterEntryView key={s.id} tag="CEP" scope={s} />)}
                {scope.pesCbos && scope.pesCbos.map((s) => <FilterEntryView key={s.id} tag="CBO" scope={s} />)}
            </FilterGroup>
        )
    }
}
const FilterForPessoaView = classToFComponent(FilterForPessoaViewClass)

//////////////

type FilterForSocioViewProps = {
    className?: string
    scope: OverlaySocioScope
}

class FilterForSocioViewClass implements FCClassContext<FilterForSocioViewProps> {
    scope!: OverlaySocioScope

    // :: Methods

    render({ className, scope }: FilterForSocioViewProps) {
        this.scope = scope

        return (
            <FilterGroup className={className} title="Sócio">
                {scope.socios && scope.socios.map((s) => <FilterEntryView key={s.id} tag="Nome" scope={s} />)}
            </FilterGroup>
        )
    }
}
const FilterForSocioView = classToFComponent(FilterForSocioViewClass)

//////////////

type FilterForCnesViewProps = {
    className?: string
    scope: OverlayCnesScope
}

class FilterForCnesViewClass implements FCClassContext<FilterForCnesViewProps> {
    scope!: OverlayCnesScope

    // :: Methods

    render({ className, scope }: FilterForCnesViewProps) {
        this.scope = scope

        // prettier-ignore
        return (
            <FilterGroup className={className} title="Estab. de saúde">
                {scope.cnes && scope.cnes.map((s) => <FilterEntryView key={s.id} tag="CNES" scope={s} />)}
                {scope.cnesTipo && scope.cnesTipo.map((s) => <FilterEntryView key={s.id} tag="Tipo" scope={s} />)}
                {scope.esaComplexidade && scope.esaComplexidade.map((s) => <FilterEntryView key={s.id} tag="Complexidade" scope={s} />)}
                {scope.esaLeitosPorte && scope.esaLeitosPorte.map((s) => <FilterEntryView key={s.id} tag="Leitos Porte" scope={s} />)}
                {scope.esaLeitosQtd && scope.esaLeitosQtd.map((s) => <FilterEntryView key={s.id} tag="Leitos Qtd" scope={s} />)}
                {scope.cnesEnderecos && scope.cnesEnderecos.map((s) => <FilterEntryView key={s.id} tag="Endereço" scope={s} />)}
                {scope.cnesLogradouros && scope.cnesLogradouros.map((s) => <FilterEntryView key={s.id} tag="Rua" scope={s} />)}
                {scope.cnesBairros && scope.cnesBairros.map((s) => <FilterEntryView key={s.id} tag="Bairro" scope={s} />)}
                {scope.cnesMunicipios && scope.cnesMunicipios.map((s) => <FilterEntryView key={s.id} tag="Cidade" scope={s} />)}
                {scope.cnesCeps && scope.cnesCeps.map((s) => <FilterEntryView key={s.id} tag="CEP" scope={s} />)}
                {scope.cnesUfs && scope.cnesUfs.map((s) => <FilterEntryView key={s.id} tag="UF" scope={s} />)}
            </FilterGroup>
        )
    }
}
const FilterForCnesView = classToFComponent(FilterForCnesViewClass)

//////////////

type FilterForPessoaAndSocioViewProps = {
    className?: string
    scope: OverlayPessoaAndSocioScope
}

class FilterForPessoaAndSocioViewClass implements FCClassContext<FilterForPessoaAndSocioViewProps> {
    scope!: OverlayPessoaAndSocioScope

    // :: Methods

    render({ className, scope }: FilterForPessoaAndSocioViewProps) {
        this.scope = scope

        // prettier-ignore
        return (
            <FilterGroup className={className} title="Pessoa e sócio">
                {scope.crms && scope.crms.map((s) => <FilterEntryView key={s.id} tag="CRM" scope={s} />)}
                {scope.crmUfs && scope.crmUfs.map((s) => <FilterEntryView key={s.id} tag="CRM UF" scope={s} />)}
                {scope.crmDataInscricao && scope.crmDataInscricao.map((s) => <FilterEntryView key={s.id} tag="CRM Dta Inscr." scope={s} />)}
                {scope.espMeds && scope.espMeds.map((s) => <FilterEntryView key={s.id} tag="Esp. Médica" scope={s} />)}
                {scope.cids && scope.cids.map((s) => <FilterEntryView key={s.id} tag="CID" scope={s} />)}
                {scope.atos && scope.atos.map((s) => <FilterEntryView key={s.id} tag="Ato" scope={s} />)}
                {scope.atoCbos && scope.atoCbos.map((s) => <FilterEntryView key={s.id} tag="CBO do Ato" scope={s} />)}
                {scope.cpfs && scope.cpfs.map((s) => <FilterEntryView key={s.id} scope={s} tag="Pessoa" />)}
            </FilterGroup>
        )
    } //
}
const FilterForPessoaAndSocioView = classToFComponent(FilterForPessoaAndSocioViewClass, React)

//////////////

type FilterForOperadoraViewProps = {
    className?: string
    scope: OverlayOperadoraScope
}

class FilterForOperadoraViewClass implements FCClassContext<FilterForOperadoraViewProps> {
    scope!: OverlayOperadoraScope

    // :: Methods

    render({ className, scope }: FilterForOperadoraViewProps) {
        this.scope = scope

        return (
            <FilterGroup className={className} title="Operadoras">
                {scope.empOps && scope.empOps.map((s) => <FilterEntryView key={s.id} tag="Oper. Empresa" scope={s} />)}
                {scope.prfOps && scope.prfOps.map((s) => <FilterEntryView key={s.id} tag="Oper. Médico" scope={s} />)}
            </FilterGroup>
        )
    }
}
const FilterForOperadoraView = classToFComponent(FilterForOperadoraViewClass)
